import React, { ReactElement } from 'react';
import IconSVG, { IconType } from './IconSVG';
import Button from './Button';

interface EmptyStateAction {
  label: string;
  onClick: () => void;
  variant?: 'primary' | 'secondary' | 'outline';
}

interface EmptyStateProps {
  icon?: IconType;
  title: string;
  message?: string; // Make optional for backward compatibility
  description?: string; // Added for compatibility with older components
  action?: EmptyStateAction | ReactElement;
  className?: string;
}

const EmptyState: React.FC<EmptyStateProps> = ({
  icon = 'info',
  title,
  message,
  description,
  action,
  className = '',
}) => {
  // Render action button based on type (EmptyStateAction or ReactElement)
  const renderAction = () => {
    if (!action) return null;
    
    // If action is a ReactElement, render it directly
    if (React.isValidElement(action)) {
      return action;
    }
    
    // Otherwise, it's an EmptyStateAction, render a Button
    const { label, onClick, variant = 'primary' } = action as EmptyStateAction;
    return (
      <Button
        variant={variant}
        onClick={onClick}
      >
        {label}
      </Button>
    );
  };
  
  return (
    <div className={`flex flex-col items-center text-center p-8 ${className}`}>
      <div className="bg-gray-100 dark:bg-gray-700 rounded-full p-4 mb-4">
        <IconSVG icon={icon} className="w-8 h-8 text-gray-500 dark:text-gray-400" />
      </div>

      <h3 className="text-lg font-medium text-gray-800 dark:text-gray-200 mb-2">{title}</h3>

      <p className="text-gray-500 dark:text-gray-400 max-w-md mb-6">{description || message}</p>
      
      {renderAction()}
    </div>
  );
};

export default EmptyState;